import React, { useEffect } from 'react';
import { EnvelopeSimple, ChatText, Megaphone } from '@phosphor-icons/react';
import ProBadge from '../common/ProBadge';
import { TemplatePicker } from './TemplatePicker';

const firstName = (name = '') => name.trim().split(/\s+/)[0] || 'there';
const textNumber = (c) => c?.mobile || c?.phone;
const canText = (c) => Boolean(textNumber(c) && c?.sms_consent);
const smsHint = (c) => {
  if (!textNumber(c)) return 'No phone saved';
  if (!c.sms_consent) return "Hasn't agreed to texts (edit contact)";
  return textNumber(c);
};

// Ready-made message built from the task fields
export const buildContactMessage = ({ contactName, senderName, title, dueDate, time, location }) => {
  const when = dueDate
    ? new Date(`${dueDate}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
    : '';
  const clock = time ? new Date(`2000-01-01T${time}`).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '';
  let line = `Just a quick note about "${title || 'our task'}"`;
  if (when) line += ` on ${when}${clock ? ` at ${clock}` : ''}`;
  if (location) line += ` (${location})`;
  line += '.';
  return [`Hi ${firstName(contactName)},`, '', line, '', `Thanks,\n${senderName || ''}`.trim()].join('\n');
};

const Toggle = ({ checked, disabled, onChange, icon: Icon, label, hint, testId }) => (
  <label className={`flex-1 flex items-start gap-2 p-3 rounded-lg border transition-colors ${disabled ? 'opacity-50 cursor-not-allowed border-gray-200' : checked ? 'border-indigo-400 bg-indigo-50 cursor-pointer' : 'border-gray-200 hover:bg-gray-50 cursor-pointer'}`}>
    <input type="checkbox" className="mt-0.5 h-4 w-4 text-indigo-600 rounded" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
    <span className="min-w-0">
      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900"><Icon size={16} weight="duotone" />{label}</span>
      <span className="block text-xs text-gray-500 truncate">{hint}</span>
    </span>
  </label>
);

export const NotifyContactSection = ({ contact, isPro, value, onChange, template, onUpgrade, footnote = 'Sent after the task is saved. Email replies come to you.' }) => {
  // Keep the message in sync with the task fields until the user edits it
  useEffect(() => {
    if (!value.edited && value.message !== template) onChange({ ...value, message: template });
  }, [template]);

  // Switch off channels the newly picked contact can't receive
  useEffect(() => {
    if (!contact) return;
    const email = value.email && Boolean(contact.email);
    const sms = value.sms && canText(contact);
    if (email !== value.email || sms !== value.sms) onChange({ ...value, email, sms });
  }, [contact?.id]);

  if (!contact) return null;
  const name = firstName(contact.name);

  if (!isPro) {
    return (
      <button type="button" onClick={onUpgrade} className="w-full flex items-center justify-between gap-3 p-4 rounded-lg border border-indigo-200 bg-indigo-50 text-left" data-testid="notify-contact-locked">
        <span className="flex items-center gap-2 text-sm font-medium text-indigo-900"><Megaphone size={18} weight="duotone" />Let {name} know by email or SMS</span>
        <ProBadge size="sm" />
      </button>
    );
  }

  const enabled = value.email || value.sms;
  return (
    <div className="p-4 rounded-lg border border-indigo-200 bg-white space-y-3" data-testid="notify-contact-section">
      <p className="flex items-center gap-2 text-sm font-semibold text-gray-900"><Megaphone size={18} weight="duotone" className="text-indigo-600" />Let {name} know</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Toggle checked={value.email} disabled={!contact.email} onChange={(v) => onChange({ ...value, email: v })} icon={EnvelopeSimple} label="Email" hint={contact.email || 'No email saved'} testId="notify-contact-email-toggle" />
        <Toggle checked={value.sms} disabled={!canText(contact)} onChange={(v) => onChange({ ...value, sms: v })} icon={ChatText} label="SMS" hint={smsHint(contact)} testId="notify-contact-sms-toggle" />
      </div>
      {enabled && (
        <div>
          <div className="hidden lg:block mb-2">
            <TemplatePicker currentMessage={value.message} onPick={(body) => onChange({ ...value, message: body, edited: true })} />
          </div>
          <textarea
            value={value.message}
            onChange={(e) => onChange({ ...value, message: e.target.value, edited: true })}
            rows={5}
            maxLength={1000}
            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            data-testid="notify-contact-message"
          />
          <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
            <span>{footnote}</span>
            {value.sms && <span data-testid="notify-contact-sms-length">{value.message.length} chars{value.message.length > 160 ? ' · may count as more than 1 SMS' : ''}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotifyContactSection;
